import { isEmpty, get, mapValues, has, assign } from 'lodash';
import axios from 'axios';
import config from 'config';
import DialogUtil from 'utils/dialog';
import proxy from 'api/proxy';

// api modules..
import contracts from './modules/contracts';
import applications from './modules/applications';
import carTypes from './modules/carTypes';
import carGroups from './modules/carGroups';
import prices from './modules/prices';
import insurancePrices from './modules/insurancePrices';
import priceDiscountApplications from './modules/priceDiscountApplications';
import codes from './modules/codes';
import creditInquiryAgreements from './modules/creditInquiryAgreements';
import creditRatings from './modules/creditRatings';
import snsLogins from './modules/snsLogins';
import ssoLogins from './modules/ssoLogins';
import regionGroups from './modules/regionGroups';
import smsAuths from './modules/smsAuths';
import uploads from './modules/uploads';
import receipts from './modules/receipts';
import promotions from './modules/promotions';
import { isWebview } from 'utils/webview/isWebview';
import { closeWebView } from 'utils/webview/closeWebView';

function initmulitparClient(vm, createConfig) {
  const client = axios.create(createConfig);
  // request interceptor
  if (isEmpty(client.interceptors.request.handlers)) {
    client.interceptors.request.use((config) => {
      const accessToken = vm.$store.state.accessToken;
      config = assign(config, {
        headers: {
          Authorization: accessToken || null,
          'Content-Type': 'multipart/form-data',
        },
      });
      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }
  return client;
}

function initClient(vm, createConfig) {
  const client = axios.create(createConfig);

  // request interceptor
  if (isEmpty(client.interceptors.request.handlers)) {
    client.interceptors.request.use((config) => {
      const accessToken = vm.$store.state.accessToken;
      config = assign(config, {
        headers: {
          Authorization: accessToken || null,
        },
      });
      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  // response interceptor
  if (isEmpty(client.interceptors.response.handlers)) {
    client.interceptors.response.use((response) => {
      if (has(response.data, 'success')) {
        return get(response.data, 'data', '');
      } else {
        return response.data;
      }
    }, (error) => {
      const status = get(error, 'response.status');
      let { message } = get(error, 'response.data.errors[0]', { code: '', message: '' });
      switch (status) {
        case 401:
          if (isEmpty(message)) {
            message = '세션시간이 만료되었습니다. 로그인 페이지로 이동합니다.';
          }
          message = message.replace('. ', '.\n');
          DialogUtil.alert(message);
          vm.$store.dispatch('user/logout');
          vm.$store.dispatch('updateAccessToken', '');
          if (window.location.pathname === '/login') {
            vm.$router.push(`/login?redirect=${window.location.href.split('redirect=')[1]}`);
          } else {
            vm.$router.push(`/login?redirect=${window.location.pathname}?${window.location.href.split('?')[1]}`);
          }
          vm.isLoading = false;
          break;
        case 403:
          if (isEmpty(message)) {
            message = '권한이 존재하지 않습니다.';
          }
          message = message.replace('. ', '.\n');
          DialogUtil.alert(message);

          if (isWebview()) {
            closeWebView();
          } else {
            window.location.href = config.planHome.baseURL;
          }
          vm.isLoading = false;
          break;
        case 400:
          if (isEmpty(message)) {
            message = '처리중 문제가 발생 하였습니다. 다시 시도해 주세요.';
          }
          message = message.replace('. ', '.\n');
          DialogUtil.alert(message);
          vm.isLoading = false;
          break;
        case 500:
          if (!checkVerifyReferrerUrl(error.response.config.url)) {
            message = '처리중 문제가 발생 하였습니다. 다시 시도해 주세요.';
            message = message.replace('. ', '.\n');
            DialogUtil.alert(message);
            vm.isLoading = false;
          }
          break;
        case undefined:
          break;
        default:
          message = '처리중 문제가 발생 하였습니다. 다시 시도해 주세요.';
          message = message.replace('. ', '.\n');
          DialogUtil.alert(message);
          vm.isLoading = false;
          break;
      }
      return Promise.reject(error);
    });
    return client;
  }
}

function checkVerifyReferrerUrl(url) {
  return url.includes('/promotion/referrer/verify');
}

const modules = {
  CAR_TYPE: carTypes,
  CAR_GROUP: carGroups,
  INSURANCE_PRICE: insurancePrices,
  PRICE: prices,
  PRICE_DISCOUNT: priceDiscountApplications,
  CODE: codes,
  CREDIT_RATING: creditRatings,
  CREDIT_INQUIRY_AGREEMENT: creditInquiryAgreements,
  CONTRACT: contracts,
  APPLICATION: applications,
  SNSLOGIN: snsLogins,
  REGION_GROUP: regionGroups,
  SSOLOGIN: ssoLogins,
  SMS_AUTH: smsAuths,
  UPLOAD: uploads,
  RECEIPT: receipts,
  PROMOTION: promotions,
};

// { AUTH: "AUTH", ... } 의 값을 가지는 Map.
export const API = Object.keys(modules)
  .reduce((result, key) => {
    result[key] = key;
    return result;
  }, {});

/**
 * Vue Component에 mixins 형태로 사용.
 * mixins로 등록 후 아래와 같은 형태로 사용.
 *
 * import 예시..
 * `import ApiClient, { APIS } from 'api/client';`
 *
 * get api 예시.
 * `const AuthApi = this.getApi(APIS.AUTH);`
 *
 * Vue Component의 lifecycle에 따라 `beforeDestroy`시 자동으로 API 요청이 취소되며
 * `this.cancelApi()` 를 호출하면 수동으로 처리 중인 API 취소가 가능.
 */
export default {
  created() {
    const client = initClient(this, config.axios);
    const uploadClient = initmulitparClient(this, config.axios);
    const serverClient = axios.create(config.server);
    const source = axios.CancelToken.source();
    const apis = mapValues(modules, (module) => proxy(module, client));
    const uploadApis = mapValues(modules, (module) => proxy(module, uploadClient));
    const serverApis = mapValues(modules, (module) => proxy(module, serverClient));
    this.getApi = (name) => apis[name];
    this.getUploadApi = (name) => uploadApis[name];
    this.getServerApi = (name) => serverApis[name];
    this.cancelApi = source.cancel;
  },
  beforeDestroy() {
    this.cancelApi();
  },
};
